/**
 * SchedulingTypeBreakdownCard - Card com breakdown por tipo de agendamento
 * Exibe contadores de: Agendamento Online, Reunião, Tarefas
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-14 v1.0.0 - Implementação inicial
 */
import React from 'react';
import { Users, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import googleMeetIcon from '@/assets/google-meet-icon.png';

/** Props do componente */
interface SchedulingTypeBreakdownCardProps {
  /** Contagem de bookings online confirmados */
  onlineCount: number;
  /** Contagem de atividades tipo meeting */
  meetingCount: number;
  /** Contagem de atividades tipo task */
  taskCount: number;
  /** Se está carregando */
  isLoading: boolean;
}

/** Configuração de cada tipo para exibição */
const TYPE_CONFIG = [
  {
    key: 'online',
    label: 'Agendamento Online',
    icon: null as any,
    customIcon: googleMeetIcon,
    colorClass: 'text-primary',
    bgClass: 'bg-primary/10',
  },
  {
    key: 'meeting',
    label: 'Reunião',
    icon: Users,
    customIcon: null as string | null,
    colorClass: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-500/10',
  },
  {
    key: 'task',
    label: 'Lembretes',
    icon: ClipboardList,
    customIcon: null as string | null,
    colorClass: 'text-violet-600 dark:text-violet-400',
    bgClass: 'bg-violet-500/10',
  },
] as const;

/**
 * Card com breakdown de agendamentos por tipo
 */
export function SchedulingTypeBreakdownCard({
  onlineCount,
  meetingCount,
  taskCount,
  isLoading,
}: SchedulingTypeBreakdownCardProps) {
  // Mapa de contagens por chave
  const countMap: Record<string, number> = {
    online: onlineCount,
    meeting: meetingCount,
    task: taskCount,
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <Skeleton className="h-4 w-40" />
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2 px-4 pt-4">
        <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Por Tipo de Agendamento
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-1.5">
        {TYPE_CONFIG.map(({ key, label, icon: Icon, customIcon, colorClass, bgClass }) => (
          <div
            key={key}
            className="flex items-center justify-between p-2 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className={cn('h-7 w-7 rounded-md flex items-center justify-center', bgClass)}>
                {customIcon
                  ? <img src={customIcon} alt={label} className="h-4 w-4" />
                  : <Icon className={cn('h-3.5 w-3.5', colorClass)} />
                }
              </div>
              <span className="text-xs font-medium text-foreground">{label}</span>
            </div>
            <span className={cn('text-sm font-bold', colorClass)}>
              {countMap[key]}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
